import { captureException, captureMessage } from '@sentry/nextjs';
import type Hls from 'hls.js';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { videoPreviewCurrentTimeAtom } from './atoms';
export interface onReadyParams {
  videoPlayer?: HTMLVideoElement | null;
}

/**
 * Without `startLevel`, hls.js has no level details to run ABR against on the
 * first fragment, so it falls back to the first rendition listed in the master
 * playlist — which for our previews is 720p (a ~900KB opening segment). Levels
 * are sorted low-to-high internally, so level 0 is the smallest rendition
 * (~108KB) and the first frame paints in a fraction of the time.
 *
 * `capLevelToPlayerSize` then keeps ABR from immediately upswitching to 720p
 * for a tile that is only a few hundred pixels wide.
 */
const PREVIEW_HLS_CONFIG = {
  capLevelToPlayerSize: true,
  startLevel: 0
} as const;
export const VideoPreviewPlayer = ({
  src,
  isPlaying,
  onReady,
  setIsVideoPlaying
}: {
  src: string;
  isPlaying: boolean;
  onReady: (params?: onReadyParams) => void;
  setIsVideoPlaying: (isVideoPlaying: boolean) => void;
}) => {
  const ref = useRef<HTMLVideoElement>(null);
  const setCurrentTime = useSetAtom(videoPreviewCurrentTimeAtom);
  useEffect(() => {
    const videoPlayer = ref.current;
    if (!videoPlayer) {
      return () => {};
    }
    const playVideo = () => {
      void videoPlayer.play();
    };

    // hls.js is 152KB gz, so it is only fetched once a preview actually mounts
    // (i.e. at hover intent) rather than at pageload.
    let isCancelled = false;
    let hlsInstance: Hls | null = null;
    let hasNativeListener = false;

    // Used when hls.js is unavailable — either the browser can't use MSE, or the
    // async chunk failed to load.
    const setUpNativePlayback = () => {
      if (videoPlayer.canPlayType('application/vnd.apple.mpegurl')) {
        hasNativeListener = true;
        videoPlayer.src = src;
        videoPlayer.addEventListener('loadedmetadata', playVideo);
        return;
      }
      captureMessage('Cannot play HLS natively', {
        level: 'info',
        extra: {
          videoSrc: src,
          userAgent: navigator.userAgent
        }
      });

      // Fallback for other browsers
      videoPlayer.src = src;
      playVideo();
    };
    const setUpPlayback = async () => {
      let HlsPlayer: typeof Hls;
      try {
        HlsPlayer = (await import('hls.js')).default;
      } catch (error) {
        captureException(error, {
          extra: {
            videoSrc: src,
            context: 'hls-dynamic-import'
          }
        });
        if (!isCancelled) {
          setUpNativePlayback();
        }
        return;
      }
      if (isCancelled) {
        return;
      }
      if (HlsPlayer.isSupported()) {
        const hls = new HlsPlayer(PREVIEW_HLS_CONFIG);
        hlsInstance = hls;
        hls.loadSource(src);
        hls.attachMedia(videoPlayer);
        hls.on(HlsPlayer.Events.MANIFEST_PARSED, playVideo);
        hls.on(HlsPlayer.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            captureException(new Error(`HLS fatal error: ${data.details}`), {
              extra: {
                videoSrc: src,
                type: data.type
              }
            });
          }
        });
        return;
      }
      setUpNativePlayback();
    };
    void setUpPlayback();
    return () => {
      isCancelled = true;
      hlsInstance?.destroy();
      if (hasNativeListener) {
        videoPlayer.removeEventListener('loadedmetadata', playVideo);
      }
    };
  }, [src]);
  const handleOnReady = useCallback(() => {
    onReady?.({
      videoPlayer: ref.current
    });
  }, [onReady]);
  useEffect(() => {
    const videoPlayer = ref.current;
    if (!(videoPlayer && isPlaying)) {
      setCurrentTime(0);
      return;
    }
    const handleTimeUpdate = () => {
      setCurrentTime(videoPlayer.currentTime);
    };
    videoPlayer.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      videoPlayer.removeEventListener('timeupdate', handleTimeUpdate);
      setCurrentTime(0);
    };
  }, [isPlaying, setCurrentTime]);
  useEffect(() => () => {
    setIsVideoPlaying(false);
  }, [setIsVideoPlaying]);
  return <video autoPlay className="h-full w-full object-cover" controls={false} loop muted onCanPlay={handleOnReady} playsInline ref={ref} data-sentry-component="VideoPreviewPlayer" data-sentry-source-file="VideoPreviewPlayer.tsx" />;
};